import { z } from 'zod';
import {
  identifierSchema,
  requestIdSchema,
  serverTimestampSchema,
  studentHomeworkProgressResponseSchema,
  studentObservationResponseSchema,
  studentRoomMembersResponseSchema,
  studentWatchGamesResponseSchema,
  type StudentHomeworkProgressResponse,
  type StudentObservationResponse,
  type StudentRoomMembersResponse,
  type StudentWatchGamesResponse,
} from '@wx-weiqi/contracts';
import type { IdentityStore } from '../stores/identity-store';

export interface StudentActivityApi {
  homeworkParticipants(
    assignmentId: string,
    page: number,
    pageSize: number,
  ): Promise<StudentHomeworkProgressResponse['data']>;
  roomMembers(gameId: string): Promise<StudentRoomMembersResponse['data']>;
  watchGames(page: number, pageSize: number): Promise<StudentWatchGamesResponse['data']>;
  observe(gameId: string, viewerId: string): Promise<StudentObservationResponse['data']>;
  leaveObservation(gameId: string, viewerId: string): Promise<void>;
}

export class StudentActivityService implements StudentActivityApi {
  constructor(private readonly identity: IdentityStore) {}
  async homeworkParticipants(assignmentId: string, page: number, pageSize: number) {
    return (
      await this.identity.authorized(
        `/homework-assignments/${encodeURIComponent(assignmentId)}/participants?page=${page}&pageSize=${pageSize}`,
        studentHomeworkProgressResponseSchema,
      )
    ).data;
  }
  async roomMembers(gameId: string) {
    return (
      await this.identity.authorized(
        `/student-games/${encodeURIComponent(gameId)}/members`,
        studentRoomMembersResponseSchema,
      )
    ).data;
  }
  async watchGames(page: number, pageSize: number) {
    return (
      await this.identity.authorized(
        `/student-games?page=${page}&pageSize=${pageSize}`,
        studentWatchGamesResponseSchema,
      )
    ).data;
  }
  async observe(gameId: string, viewerId: string) {
    return (
      await this.identity.authorized(
        `/student-games/${encodeURIComponent(gameId)}/observation`,
        studentObservationResponseSchema,
        { method: 'PUT', body: { viewerId } },
      )
    ).data;
  }
  async leaveObservation(gameId: string, viewerId: string) {
    await this.identity.authorized(
      `/student-games/${encodeURIComponent(gameId)}/observation`,
      z.object({
        requestId: requestIdSchema,
        gameId: identifierSchema,
        serverTimestamp: serverTimestampSchema,
      }),
      { method: 'DELETE', body: { viewerId } },
    );
  }
}
